import { type FormEvent, type ReactNode, useState } from 'react'
import { useLocation } from '../../../features/locations/use-location'
import {
  canEditWanSettings,
  useSaveWanSettings,
  useWanSettings,
  type WanDataRecord,
  type WanSettingsData,
  type WanSettingsUpdate,
} from '../../../features/locations/use-wan-settings'
import { ApiError } from '../../../lib/api-client'
import { useIdOrCode } from '../../use-id-or-code'
import { Header, LedgerLink, locPath, mono, Note, Section, SkeletonRows, Status, serif } from './ui'

const th = 'py-2.5 pr-4 text-left text-[12px] font-medium text-[var(--color-muted)]'
const input =
  'w-full border-0 border-b border-[var(--color-border)] bg-transparent pb-1 pt-0.5 text-[14px] text-[var(--color-fg)] placeholder:italic placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children and sits inside this label
    <label className="block">
      <span className="mb-1 block text-[12px] text-[var(--color-muted)]">{label}</span>
      {children}
    </label>
  )
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'You do not hold permission to change WAN settings for this store.'
    if (e.status === 400) return 'The entry was refused. Check the values, and that the store is fully provisioned.'
    return `The change could not be filed (error ${e.status}). Try again.`
  }
  return 'The change could not be filed. Try again.'
}

function SaveRow({
  canEdit,
  dirty,
  pending,
  ok,
  error,
}: {
  canEdit: boolean
  dirty: boolean
  pending: boolean
  ok: boolean
  error: unknown
}) {
  return (
    <div className="flex flex-wrap items-center gap-5 py-5">
      <button
        type="submit"
        disabled={!canEdit || !dirty || pending}
        className="cursor-pointer bg-[var(--color-accent)] px-5 py-2 text-[14px] font-semibold text-[var(--color-accent-fg)] transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {pending ? 'Filing…' : 'File changes'}
      </button>
      <span aria-live="polite" className="text-[14px] italic" style={serif}>
        {error ? (
          <span role="alert" className="text-[var(--color-accent)]">
            {errorMessage(error)}
          </span>
        ) : ok ? (
          <span className="text-[var(--v3-ok)]">Filed. The register has been updated.</span>
        ) : null}
      </span>
    </div>
  )
}

interface MerakiDraft {
  enabled: boolean
  carrier: string
  mode: 'static' | 'dynamic'
  address: string
  gateway: string
}

const RECORD_FOR_IFACE: Record<string, WanDataRecord['type']> = { wan1: 'PRIMARY', wan2: 'SECONDARY' }

function MerakiForm({ data, locationId, canEdit }: { data: WanSettingsData; locationId: string; canEdit: boolean }) {
  const save = useSaveWanSettings()
  const vs = data.vendorState
  const ifaces = vs.vendor === 'meraki' ? (vs.interfaces ?? {}) : {}
  const initial = (): Record<string, MerakiDraft> =>
    Object.fromEntries(
      Object.entries(ifaces).map(([name, i]) => [
        name,
        {
          enabled: i.enabled ?? false,
          carrier: data.dbRecords.find((r) => r.type === RECORD_FOR_IFACE[name])?.carrier ?? '',
          mode: i.assignmentMode === 'dynamic' ? 'dynamic' : 'static',
          address: i.address ?? '',
          gateway: i.gateway ?? '',
        } satisfies MerakiDraft,
      ]),
    )
  const [base, setBase] = useState(initial)
  const [draft, setDraft] = useState(initial)
  const names = Object.keys(draft)
  const dirtyNames = names.filter((n) => JSON.stringify(draft[n]) !== JSON.stringify(base[n]))
  const invalid = dirtyNames.some(
    (n) => draft[n]?.mode === 'static' && draft[n].enabled && !(draft[n].address && draft[n].gateway),
  )

  const patch = (n: string, p: Partial<MerakiDraft>) => {
    save.reset()
    setDraft((d) => ({ ...d, [n]: { ...(d[n] as MerakiDraft), ...p } }))
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!canEdit || dirtyNames.length === 0 || invalid) return
    const interfaces: Extract<WanSettingsUpdate, { vendor: 'meraki' }>['interfaces'] = {}
    for (const n of dirtyNames) {
      const d = draft[n] as MerakiDraft
      interfaces[n] = {
        enabled: d.enabled,
        ...(d.carrier ? { carrier: d.carrier } : {}),
        svis: {
          ipv4:
            d.mode === 'dynamic'
              ? { assignmentMode: 'dynamic' }
              : { assignmentMode: 'static', address: d.address, gateway: d.gateway },
        },
      }
    }
    save.mutate({ locationId, body: { vendor: 'meraki', interfaces } }, { onSuccess: () => setBase(draft) })
  }

  if (names.length === 0) return <Note>The vendor reports no configurable interfaces.</Note>

  return (
    <form onSubmit={submit}>
      <fieldset disabled={!canEdit} className="m-0 border-0 p-0">
        {names.map((n) => {
          const d = draft[n] as MerakiDraft
          return (
            <div key={n} className="grid gap-x-6 gap-y-4 border-b border-[var(--color-border)] py-5 sm:grid-cols-6">
              <div className="sm:col-span-6 flex items-baseline justify-between">
                <h3 className="text-[16px] font-semibold" style={mono}>
                  {n}
                </h3>
                <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={d.enabled}
                    onChange={(e) => patch(n, { enabled: e.target.checked })}
                    className="accent-[var(--color-accent)]"
                  />
                  Enabled
                </label>
              </div>
              <div className="sm:col-span-2">
                <Field label="Carrier">
                  <input className={input} value={d.carrier} onChange={(e) => patch(n, { carrier: e.target.value })} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Addressing">
                  <select
                    className={input}
                    value={d.mode}
                    onChange={(e) => patch(n, { mode: e.target.value === 'dynamic' ? 'dynamic' : 'static' })}
                  >
                    <option value="static">Static</option>
                    <option value="dynamic">Dynamic (DHCP)</option>
                  </select>
                </Field>
              </div>
              {d.mode === 'static' ? (
                <>
                  <div className="sm:col-span-3">
                    <Field label="Address (CIDR)">
                      <input
                        className={input}
                        style={mono}
                        value={d.address}
                        placeholder="203.0.113.10/30"
                        onChange={(e) => patch(n, { address: e.target.value })}
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-3">
                    <Field label="Gateway">
                      <input
                        className={input}
                        style={mono}
                        value={d.gateway}
                        placeholder="203.0.113.9"
                        onChange={(e) => patch(n, { gateway: e.target.value })}
                      />
                    </Field>
                  </div>
                </>
              ) : null}
            </div>
          )
        })}
      </fieldset>
      {invalid ? (
        <p className="pt-3 text-[13px] italic text-[var(--color-accent)]" style={serif}>
          A static, enabled interface needs both an address and a gateway.
        </p>
      ) : null}
      <SaveRow
        canEdit={canEdit && !invalid}
        dirty={dirtyNames.length > 0}
        pending={save.isPending}
        ok={save.isSuccess && dirtyNames.length === 0}
        error={save.error}
      />
    </form>
  )
}

function CloudflareForm({
  data,
  locationId,
  canEdit,
}: {
  data: WanSettingsData
  locationId: string
  canEdit: boolean
}) {
  const save = useSaveWanSettings()
  const vs = data.vendorState
  const wans = (vs.vendor === 'cloudflare' ? vs.wans : null) ?? []
  const [wanId, setWanId] = useState(wans[0]?.id ?? '')
  const current = wans.find((w) => w.id === wanId)
  const sa = current?.static_addressing
  const [carrier, setCarrier] = useState('')
  const [address, setAddress] = useState(sa?.address ?? '')
  const [gateway, setGateway] = useState(sa?.gateway_address ?? '')
  const [secondary, setSecondary] = useState(sa?.secondary_address ?? '')
  const dirty =
    carrier !== '' ||
    address !== (sa?.address ?? '') ||
    gateway !== (sa?.gateway_address ?? '') ||
    secondary !== (sa?.secondary_address ?? '')

  if (wans.length === 0) return <Note>The vendor reports no configurable WANs.</Note>

  const pick = (id: string) => {
    save.reset()
    setWanId(id)
    const s = wans.find((w) => w.id === id)?.static_addressing
    setAddress(s?.address ?? '')
    setGateway(s?.gateway_address ?? '')
    setSecondary(s?.secondary_address ?? '')
    setCarrier('')
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!canEdit || !wanId || !address || !gateway) return
    save.mutate({
      locationId,
      body: {
        vendor: 'cloudflare',
        wanId,
        ...(carrier ? { carrier } : {}),
        staticAddressing: { address, gateway_address: gateway, ...(secondary ? { secondary_address: secondary } : {}) },
      },
    })
  }

  const touch = (fn: (v: string) => void) => (e: { target: { value: string } }) => {
    save.reset()
    fn(e.target.value)
  }

  return (
    <form onSubmit={submit}>
      <fieldset
        disabled={!canEdit}
        className="m-0 grid gap-x-6 gap-y-4 border-0 border-b border-[var(--color-border)] p-0 py-5 sm:grid-cols-6"
      >
        <div className="sm:col-span-3">
          <Field label="WAN">
            <select className={input} value={wanId} onChange={(e) => pick(e.target.value)}>
              {wans.map((w, i) => (
                <option key={w.id ?? i} value={w.id ?? ''}>
                  {w.name ?? w.id ?? `WAN ${i + 1}`}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-3">
          <Field label="Carrier">
            <input className={input} value={carrier} placeholder="unchanged" onChange={touch(setCarrier)} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Address">
            <input className={input} style={mono} value={address} onChange={touch(setAddress)} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Gateway">
            <input className={input} style={mono} value={gateway} onChange={touch(setGateway)} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Secondary address">
            <input
              className={input}
              style={mono}
              value={secondary}
              placeholder="optional"
              onChange={touch(setSecondary)}
            />
          </Field>
        </div>
      </fieldset>
      <SaveRow
        canEdit={canEdit && address !== '' && gateway !== ''}
        dirty={dirty}
        pending={save.isPending}
        ok={save.isSuccess && !dirty}
        error={save.error}
      />
    </form>
  )
}

export function LedgerWanSettings() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const { data, isLoading, error } = useWanSettings(idOrCode)

  const header = (
    <Header
      eyebrow={
        <>
          <LedgerLink to="/locations">Locations</LedgerLink> /{' '}
          <LedgerLink to={locPath(idOrCode)}>{idOrCode}</LedgerLink> /{' '}
          <LedgerLink to={`${locPath(idOrCode)}/network`}>Network</LedgerLink> / WAN
        </>
      }
      title="WAN settings"
      standfirst="The circuits entering this store, and the addressing the edge device holds for them."
    />
  )

  if (isLoading || location.isLoading)
    return (
      <article>
        {header}
        <SkeletonRows />
      </article>
    )
  if (error || !data)
    return (
      <article>
        {header}
        <Note tone="error">
          {error ? `The settings could not be read: ${error.message}` : 'No WAN settings on record.'}
        </Note>
      </article>
    )

  const canEdit = canEditWanSettings(data)
  const locationId = location.data?.id ?? ''

  return (
    <article>
      {header}

      <Section
        title="Circuits"
        note={
          data.routerSerial
            ? `Edge: ${data.edgeVendor ?? 'unknown'}, serial ${data.routerSerial}.`
            : (data.edgeVendor ?? undefined)
        }
        aside={data.lifecycle ? <span className="text-[var(--color-muted)]">{data.lifecycle}</span> : null}
      >
        {data.dbRecords.length === 0 ? (
          <Note>No circuits are recorded for this store.</Note>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th scope="col" className={th}>
                  Role
                </th>
                <th scope="col" className={th}>
                  Carrier
                </th>
                <th scope="col" className={th}>
                  Addressing
                </th>
                <th scope="col" className={th}>
                  Address
                </th>
                <th scope="col" className={th}>
                  Gateway
                </th>
                <th scope="col" className={`${th} text-right`}>
                  Link
                </th>
              </tr>
            </thead>
            <tbody>
              {data.dbRecords.map((r) => (
                <tr key={r.id} className="h-10 border-b border-[var(--color-border)] text-[14px]">
                  <td className="pr-4 capitalize" style={serif}>
                    {r.type.toLowerCase()}
                  </td>
                  <td className="pr-4">{r.carrier || '—'}</td>
                  <td className="pr-4 capitalize">{r.assignmentType.toLowerCase()}</td>
                  <td className="pr-4 text-[13px]" style={mono}>
                    {r.assignedCidr ?? r.assignedIp ?? '—'}
                  </td>
                  <td className="pr-4 text-[13px]" style={mono}>
                    {r.gw ?? '—'}
                  </td>
                  <td className="text-right">
                    <Status label={r.status ? 'Up' : 'Down'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Amend">
        {!canEdit ? (
          <p
            className="mt-3 border-l-2 border-[var(--color-accent)] pl-4 text-[14.5px] italic text-[var(--color-muted)]"
            style={serif}
          >
            Editing is closed. Changes are accepted only once a store is provisioned; this one is{' '}
            {data.lifecycle ? data.lifecycle.toLowerCase() : 'not in a managed lifecycle'}.
          </p>
        ) : null}
        {data.vendorState.vendor === 'meraki' ? (
          <MerakiForm data={data} locationId={locationId} canEdit={canEdit && locationId !== ''} />
        ) : data.vendorState.vendor === 'cloudflare' ? (
          <CloudflareForm data={data} locationId={locationId} canEdit={canEdit && locationId !== ''} />
        ) : (
          <Note>This edge vendor is not supported for editing here.</Note>
        )}
      </Section>
    </article>
  )
}
